import {
  MIN_DRAW_WIDTH_PX,
  overlayPosToMs,
} from 'features/timeSeries/components/chartOverlays/overlayPointer'
import {
  applyEdgeResize,
  LabelRange,
  normalizeRange,
} from 'features/timeSeries/components/labelTool/labelGeometry'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import uPlot from 'uplot'

/** Below this a drag reads as a click, not a region. */

interface UseRangeDragProps {
  uPlotRef: React.RefObject<uPlot | null>
  isActive: boolean
  getTargetRange: (id: string) => LabelRange | null
  onDraftComplete: (range: LabelRange) => void
  onResizeCommit: (id: string, range: LabelRange) => void
}

/**
 * Drag-to-range pointer machine for chart overlays: draw a new region,
 * or grab a persisted region's edge and resize it (dragging an edge past
 * its opposite swaps the grabbed edge). Commits once, on release.
 */
export function useRangeDrag({
  uPlotRef,
  isActive,
  getTargetRange,
  onDraftComplete,
  onResizeCommit,
}: UseRangeDragProps) {
  const [draft, setDraft] = useState<LabelRange | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [resizingEdge, setResizingEdge] = useState<'start' | 'end' | null>(null)
  const [resizePreview, setResizePreview] = useState<
    (LabelRange & { id: string }) | null
  >(null)

  const dragStartMsRef = useRef<number | null>(null)
  const dragStartPxRef = useRef<number | null>(null)

  const clearDrag = useCallback(() => {
    setIsDragging(false)
    setResizingEdge(null)
    dragStartMsRef.current = null
    dragStartPxRef.current = null
  }, [])

  useEffect(() => {
    if (isActive) return
    setDraft(null)
    setResizePreview(null)
    clearDrag()
  }, [isActive, clearDrag])

  const posToMs = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) =>
      overlayPosToMs(uPlotRef.current, e),
    [uPlotRef]
  )

  const handleOverlayMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.button !== 0) return
      const position = posToMs(e)
      if (!position) return
      dragStartMsRef.current = position.valueMs
      dragStartPxRef.current = position.pixelX
      setDraft({ startMs: position.valueMs, endMs: position.valueMs })
      setIsDragging(true)
      setResizingEdge(null)
      setResizePreview(null)
      e.preventDefault()
    },
    [posToMs]
  )

  const handleEdgeMouseDown = useCallback(
    (id: string, edge: 'start' | 'end', e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      const range = getTargetRange(id)
      if (!range) return
      setResizePreview({ id, startMs: range.startMs, endMs: range.endMs })
      setResizingEdge(edge)
      setIsDragging(true)
    },
    [getTargetRange]
  )

  const handleOverlayMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging) return
      const position = posToMs(e)
      if (!position) return

      if (resizingEdge && resizePreview) {
        const next = applyEdgeResize(
          resizePreview,
          resizingEdge,
          position.valueMs
        )
        setResizePreview({
          ...resizePreview,
          startMs: next.startMs,
          endMs: next.endMs,
        })
        if (next.edge !== resizingEdge) setResizingEdge(next.edge)
        return
      }

      const startMs = dragStartMsRef.current
      if (startMs === null) return
      setDraft(normalizeRange(startMs, position.valueMs))
    },
    [isDragging, posToMs, resizePreview, resizingEdge]
  )

  const commitResize = useCallback(() => {
    const preview = resizePreview
    setResizePreview(null)
    if (!preview) return
    const current = getTargetRange(preview.id)
    if (
      current &&
      current.startMs === preview.startMs &&
      current.endMs === preview.endMs
    ) {
      return
    }
    onResizeCommit(preview.id, {
      startMs: preview.startMs,
      endMs: preview.endMs,
    })
  }, [getTargetRange, onResizeCommit, resizePreview])

  const handleOverlayMouseUp = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging) return

      if (resizingEdge) {
        commitResize()
        clearDrag()
        return
      }

      const position = posToMs(e)
      const startPx = dragStartPxRef.current
      const startMs = dragStartMsRef.current
      clearDrag()
      setDraft(null)

      if (!position || startPx === null || startMs === null) return
      if (Math.abs(position.pixelX - startPx) < MIN_DRAW_WIDTH_PX) return
      onDraftComplete(normalizeRange(startMs, position.valueMs))
    },
    [
      clearDrag,
      commitResize,
      isDragging,
      onDraftComplete,
      posToMs,
      resizingEdge,
    ]
  )

  const handleOverlayMouseLeave = useCallback(() => {
    if (!isDragging) return
    if (resizingEdge) commitResize()
    else setDraft(null)
    clearDrag()
  }, [clearDrag, commitResize, isDragging, resizingEdge])

  const getRenderedRange = useCallback(
    (id: string, range: LabelRange): LabelRange =>
      resizePreview !== null && resizePreview.id === id ? resizePreview : range,
    [resizePreview]
  )

  return {
    draft,
    isDragging,
    resizingEdge,
    getRenderedRange,
    handleOverlayMouseDown,
    handleOverlayMouseMove,
    handleOverlayMouseUp,
    handleOverlayMouseLeave,
    handleEdgeMouseDown,
  }
}
